import { useSelector } from 'react-redux'
import { Client } from '../redux/reducers/clients'
import { ClientSortOptions } from '../redux/reducers/filters'
import { RootState } from '../redux/reducers/root'
import { useExchangeRate } from './useExchangeRate'
import { computeClientDebtCzk, computeClientLtv } from '../utils/ltv'

export const useClientFilters = () => {
  const clients: Client[] = useSelector((state: RootState) => state.clients.clients)
  const filters = useSelector((state: RootState) => state.filters)
  const rates = useSelector((state: RootState) => state.currency.rates)
  const exchangeRate = useExchangeRate()

  let filteredClients: Client[] = []

  const matchTargetMarket = (client: Client): boolean => {
    if (filters.searchedInvestmentHorizonte && String(client.investmentHorizonte ?? '') !== filters.searchedInvestmentHorizonte) return false
    if (filters.searchedRiskProfile && String(client.riskProfile ?? '') !== filters.searchedRiskProfile) return false
    if (filters.searchedSkillAndExperience && (client.skillAndExperience ?? '') !== filters.searchedSkillAndExperience) return false
    if (filters.searchedPreference && (client.preference ?? '') !== filters.searchedPreference) return false
    if (filters.searchedLossAbility && (client.lossAbility ?? '') !== filters.searchedLossAbility) return false
    if (filters.searchedSuitability && (client.suitability ?? '') !== filters.searchedSuitability) return false
    return true
  }

  if (Array.isArray(clients)) {
    filteredClients = clients.filter(
      (client: Client) => {
        const nameMatch = client.company
          ? client.companyName?.toLowerCase().includes(filters.searchedClientName.toLowerCase())
          : `${client.firstName ? client.firstName : ''}${client.surname}`.toLowerCase().includes(filters.searchedClientName.toLowerCase())
        return nameMatch && matchTargetMarket(client)
      }
    )
  }

  const sumAmounts = (client: Client): number =>
    client.portfolios.reduce((sum, p) => sum + (p.amount || 0), 0)

  const sumAccountsBalanceCzk = (client: Client): number =>
    client.portfolios.reduce((sum, p) => sum + exchangeRate(p.currency, 'CZK', p.accountsBalance || 0, rates), 0)

  const ltv = (client: Client): number => computeClientLtv(client.portfolios, exchangeRate, rates)
  const debt = (client: Client): number => computeClientDebtCzk(client.portfolios, exchangeRate, rates)

  if (filteredClients && filteredClients.length > 0) {
    switch (filters.sortBy) {
      case ClientSortOptions.Az:
        filteredClients.sort((a, b) => (a.surname || '').localeCompare(b.surname || ''))
        break
      case ClientSortOptions.Za:
        filteredClients.sort((a, b) => (b.surname || '').localeCompare(a.surname || ''))
        break
      case ClientSortOptions.AmountUp:
        filteredClients.sort((a, b) => sumAmounts(a) - sumAmounts(b))
        break
      case ClientSortOptions.AmountDown:
        filteredClients.sort((a, b) => sumAmounts(b) - sumAmounts(a))
        break
      case ClientSortOptions.BalanceUp:
        filteredClients.sort((a, b) => sumAccountsBalanceCzk(a) - sumAccountsBalanceCzk(b))
        break
      case ClientSortOptions.BalanceDown:
        filteredClients.sort((a, b) => sumAccountsBalanceCzk(b) - sumAccountsBalanceCzk(a))
        break
      case ClientSortOptions.LtvUp:
        filteredClients.sort((a, b) => ltv(a) - ltv(b))
        break
      case ClientSortOptions.LtvDown:
        filteredClients.sort((a, b) => ltv(b) - ltv(a))
        break
      case ClientSortOptions.DebtUp:
        filteredClients.sort((a, b) => debt(a) - debt(b) || sumAmounts(a) - sumAmounts(b))
        break
      case ClientSortOptions.DebtDown:
        filteredClients.sort((a, b) => debt(b) - debt(a) || sumAmounts(b) - sumAmounts(a))
    }
  }

  return filteredClients
}
